import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { HelpTip, InfoButton, SwitchInput } from "@/components/admin/cards";
import { MenuButton, menuPreview, MenuRecord, MenuRecordKey } from "./menuButtons";

interface MenuButtonsEditorProps {
  buttons: MenuButton[];
  /** Key topic buttons (see ``keyTopicButtons``), shown after the fixed buttons in the preview. */
  keyTopics?: MenuButton[];
  onChange: (key: MenuRecordKey, patch: Partial<MenuRecord>) => void;
  /** Opens the example screenshot for a button. */
  onInfo?: (name: string) => void;
  labelRefs?: Partial<Record<MenuRecordKey, React.Ref<HTMLInputElement>>>;
}

const INFO_NAMES: Partial<Record<MenuRecordKey, string>> = {
  cancel_modify_booking: "cancel_modify",
  contact_us: "contact",
  talk_to_agent: "agent",
};

const cell = "border-y border-border bg-card px-2 py-2 align-middle first:rounded-l-lg first:border-l first:pl-3 last:rounded-r-lg last:border-r last:pr-3";

const MenuButtonsEditor = ({ buttons, keyTopics = [], onChange, onInfo, labelRefs }: MenuButtonsEditorProps) => {
  const preview = menuPreview(buttons, keyTopics);
  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[26rem] border-separate border-spacing-y-1.5 text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th scope="col" className="px-2 pl-3 font-semibold">
                <span className="inline-flex items-center gap-1">
                  Button
                  <HelpTip label="Menu buttons">The chatbot shows these buttons from the settings above.</HelpTip>
                </span>
              </th>
              <th scope="col" className="px-2 font-semibold">
                <span className="inline-flex items-center gap-1">
                  Label
                  <HelpTip label="Button label">Leave a label blank to use the default.</HelpTip>
                </span>
              </th>
              <th scope="col" className="w-24 px-2 font-semibold">
                <span className="inline-flex items-center gap-1">
                  Position
                  <HelpTip label="Button position">
                    Lower positions come first; key topics follow them (set their order with Order Manage on the Key Topics tab).
                  </HelpTip>
                </span>
              </th>
              <th scope="col" className="w-14 px-2 pr-3 font-semibold">Show</th>
            </tr>
          </thead>
          <tbody>
            {buttons.map((button) => {
              const key = button.record;
              const id = `menuButton_${button.key}`;
              const info = key && INFO_NAMES[key];
              return (
                <tr key={button.key} className={cn(!button.shown && "[&>*]:bg-muted/40")}>
                  <th scope="row" className={cn(cell, "text-left font-medium whitespace-nowrap")}>
                    <span className="inline-flex items-center gap-1">
                      {button.name}
                      {info && onInfo && <InfoButton label={`${button.name} example`} onClick={() => onInfo(info)} />}
                    </span>
                  </th>
                  <td className={cell}>
                    {key ? (
                      <Input id={`${id}Label`} className="h-8" aria-label={`${button.name} label`}
                        placeholder={button.defaultLabel} ref={labelRefs?.[key]}
                        value={button.customLabel ?? ""}
                        onChange={(e) => onChange(key, { label: e.target.value })} />
                    ) : (
                      <span>{button.label} <span className="text-muted-foreground">(edit under Reservation types)</span></span>
                    )}
                    <span className={cn("mt-1 block text-xs", button.shown ? "text-primary" : "text-muted-foreground")}>
                      {button.shown ? "Shown" : `Hidden: ${button.hiddenReason}`}
                    </span>
                  </td>
                  <td className={cell}>
                    {key ? (
                      <Input id={`${id}Order`} type="number" min={0} step={1} className="h-8"
                        aria-label={`${button.name} position`} value={button.order}
                        onChange={(e) => {
                          const order = e.target.value === "" ? "" : Math.max(0, Math.trunc(Number(e.target.value)));
                          onChange(key, { orderId: order, order_id: order === "" ? undefined : String(order) });
                        }} />
                    ) : (
                      <span className="px-3">{button.order}</span>
                    )}
                  </td>
                  <td className={cell}>
                    {key ? (
                      <SwitchInput id={`${id}Enabled`} aria-label={`Show ${button.name} button`} checked={button.enabled}
                        onChange={(e) => onChange(key, { enabled: e.target.checked })} />
                    ) : (
                      <span className="text-xs text-muted-foreground">Always</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground" aria-live="polite">
        Menu order: {preview.length ? preview.map((button) => button.label).join(" · ") : "no buttons"}
      </p>
    </div>
  );
};

export default MenuButtonsEditor;
